import { Component, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfirmationService, MessageService } from "primeng/api";
import { AbstractListController } from "../../common/abstract-list.component";
import { PARAM_TICKETS_PATH } from "../../constants/constants";
import { Ticket } from "src/app/models/ticket";
import { TicketsService } from "src/app/services/tickets.service";
import { TicketMessage } from "src/app/models/ticket-message";
import { TicketMessagesService } from "src/app/services/ticket-messages.service";
import { CommonModule } from "@angular/common";
import { ToastModule } from "primeng/toast";
import { DataTableComponent } from "../../common/data-table/data-table.component";
import {
  RowAction,
  TableColumn,
  TableFilter,
} from "../../common/data-table/data-table.model";

@Component({
  standalone: true,
  selector: "app-manage-tickets",
  templateUrl: "./manage-tickets.component.html",
  imports: [CommonModule, ToastModule, DataTableComponent],
})
export class ManageTicketsComponent
  extends AbstractListController<Ticket, any>
  implements OnDestroy
{
  title = "Tickets";
  pageUrl = PARAM_TICKETS_PATH;

  ticketDialog = false;
  selectedTicket: Ticket | null = null;
  replyMessage = "";
  ticketStatus = "";
  savingReply = false;
  replyDirty = false;
  hasCustomerUpdate = false;
  currentUser: any;
  conversation: TicketMessage[] = [];

  private lastCustomerMessageSnapshot = "";
  private refreshTimer: any = null;

  statusOptions = [
    { label: "Open", value: "open" },
    { label: "In Progress", value: "in_progress" },
    { label: "Closed", value: "closed" },
  ];

  tableColumns: TableColumn[] = [
    { field: "id", header: "ID", width: "80px" },
    {
      field: "subject",
      header: "Subject",
      value: (row) => this.getTicketSubject(row),
    },
    {
      field: "category",
      header: "Category",
      value: (row) => row.category || "-",
    },
    { field: "name", header: "User", value: (row) => row.name || "-" },
    { field: "email", header: "Email", value: (row) => row.email || "-" },
    {
      field: "status",
      header: "Status",
      type: "tag",
      tag: (row) => ({
        label: row.status || "open",
        severity: this.getStatusSeverity(row.status) as any,
      }),
    },
  ];

  tableFilters: TableFilter[] = [
    { field: "id", header: "ID", type: "string" },
    { field: "subject", header: "Subject", type: "string" },
    { field: "category", header: "Category", type: "string" },
    { field: "name", header: "User", type: "string" },
    { field: "email", header: "Email", type: "string" },
    {
      field: "status",
      header: "Status",
      type: "select",
      options: this.statusOptions,
    },
  ];

  rowActions: RowAction[] = [
    {
      icon: "pi pi-comments",
      label: "View / Reply",
      styleClass: "p-button p-button-primary",
      link: (row) => ["/admin/tickets/detail", row.id],
    },
  ];

  constructor(
    private ticketsService: TicketsService,
    private ticketMessagesService: TicketMessagesService,
    protected confirmationService: ConfirmationService,
    router: Router,
    route: ActivatedRoute,
    private messageService: MessageService,
  ) {
    super(
      ticketsService,
      confirmationService,
      router,
      route,
      PARAM_TICKETS_PATH,
    );

    this.useTableFilters = true;

    try {
      this.currentUser = JSON.parse(
        localStorage.getItem("currentUser") || "null",
      );
    } catch {
      this.currentUser = null;
    }
  }

  openReply(ticket: Ticket): void {
    this.selectedTicket = { ...ticket };
    this.replyMessage = "";
    this.ticketStatus = ticket.status || "open";
    this.replyDirty = false;
    this.hasCustomerUpdate = false;
    this.lastCustomerMessageSnapshot = "";
    this.conversation = [];
    this.ticketDialog = true;
    this.loadConversation();
    this.startAutoRefresh();
  }

  onReplyChange(): void {
    this.replyDirty = true;
  }

  onTicketDialogHide(): void {
    this.stopAutoRefresh();
    this.selectedTicket = null;
    this.replyMessage = "";
    this.ticketStatus = "";
    this.conversation = [];
    this.replyDirty = false;
    this.hasCustomerUpdate = false;
    this.lastCustomerMessageSnapshot = "";
  }

  refreshSelectedTicket(showToast = false): void {
    if (!this.selectedTicket?.id) {
      return;
    }

    this.loadConversation(showToast);

    this.ticketsService.getId(+this.selectedTicket.id).subscribe({
      next: (data: any) => {
        const ticket = data as Ticket;
        this.selectedTicket = { ...ticket };
        this.ticketStatus = ticket.status || this.ticketStatus || "open";
      },
    });
  }

  loadConversation(showToast = false): void {
    if (!this.selectedTicket?.id) {
      return;
    }

    const params = {
      ticket_id: this.selectedTicket.id,
      _start: 0,
      _limit: 200,
      _sort: "created_at",
      _order: "asc",
    };

    this.ticketMessagesService.list(params).subscribe({
      next: (rows) => {
        this.conversation = rows || [];

        const customerMessages = this.conversation.filter(
          (m) => m.sender_type === "customer",
        );
        const lastCustomer = customerMessages[customerMessages.length - 1];
        const snapshot = lastCustomer
          ? `${lastCustomer.id}|${lastCustomer.created_at}`
          : "";

        this.hasCustomerUpdate =
          !!this.lastCustomerMessageSnapshot &&
          snapshot !== this.lastCustomerMessageSnapshot;

        this.lastCustomerMessageSnapshot = snapshot;

        if (showToast) {
          this.messageService.add({
            key: "myKey1",
            severity: "info",
            summary: "Refreshed",
            detail: this.hasCustomerUpdate
              ? "Customer sent a new update"
              : "No new customer update",
          });
        }
      },
      error: () => {
        if (showToast) {
          this.messageService.add({
            key: "myKey2",
            severity: "error",
            summary: "Error",
            detail: "Unable to refresh ticket messages",
          });
        }
      },
    });
  }

  saveReply(): void {
    if (!this.selectedTicket?.id || this.savingReply) {
      return;
    }

    this.savingReply = true;

    const replyFormData = new FormData();
    replyFormData.append("ticket_id", String(this.selectedTicket.id));
    replyFormData.append("sender_type", "admin");
    replyFormData.append(
      "sender_user_id",
      String(this.currentUser?.user?.id || this.currentUser?.user_id || ""),
    );
    replyFormData.append("message", this.replyMessage || "");
    replyFormData.append("is_internal_note", "0");

    this.ticketMessagesService.create(replyFormData).subscribe({
      next: () => {
        const ticketFormData = new FormData();

        if (this.ticketStatus) {
          ticketFormData.append("status", this.ticketStatus);

          if (this.ticketStatus === "closed") {
            ticketFormData.append("closed_at", new Date().toISOString());
          } else {
            ticketFormData.append("closed_at", "");
          }
        }

        this.ticketsService
          .update(ticketFormData, +this.selectedTicket.id)
          .subscribe({
            next: () => {
              this.messageService.add({
                key: "myKey1",
                severity: "success",
                summary: "Saved",
                detail: "Ticket reply saved in conversation",
              });

              this.replyDirty = false;
              this.hasCustomerUpdate = false;
              this.ticketDialog = false;
              this.savingReply = false;
              this.stopAutoRefresh();
              this.loadItems();
              this.onTicketDialogHide();
            },
            error: (error) => {
              this.error = error;
              this.savingReply = false;
              this.messageService.add({
                key: "myKey2",
                severity: "error",
                summary: "Error",
                detail: "Reply saved, but ticket status update failed",
              });
            },
          });
      },
      error: (error) => {
        this.error = error;
        this.savingReply = false;
        this.messageService.add({
          key: "myKey2",
          severity: "error",
          summary: "Error",
          detail: "Unable to save ticket reply message",
        });
      },
    });
  }

  getStatusSeverity(status: string): string {
    const normalized = (status || "").toLowerCase();

    if (normalized === "closed") return "success";
    if (normalized === "in_progress") return "warning";

    return "info";
  }

  getTicketSubject(ticket: Ticket): string {
    return ticket?.subject || (ticket as any)?.title || "-";
  }

  getTicketMessage(ticket: Ticket): string {
    return ticket?.message || (ticket as any)?.description || "";
  }

  getTicketReply(ticket: Ticket): string {
    return ticket?.admin_reply || "";
  }

  formatMessageDate(dateValue: any): string {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? "" : date.toLocaleString();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(() => {
      if (this.ticketDialog && this.selectedTicket?.id) {
        this.refreshSelectedTicket(false);
      }
    }, 20000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
